import { useState } from "react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { menuItems } from "../../../mocks/menuItems";

const categories = ["All", "New", "AM", "PM"];

export default function MenuPreview() {
  const [ref, visible] = useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered =
    activeCategory === "All"
      ? menuItems
      : menuItems.filter((item) =>
          item.category === activeCategory ||
          (item.category === "All Day" && (activeCategory === "AM" || activeCategory === "PM"))
        );

  return (
    <section id="menu" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p
            className="text-black/40 text-xs tracking-[0.3em] uppercase mb-3"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            Signature Selections
          </p>
          <h2
            className="text-black text-5xl md:text-6xl font-black uppercase leading-none mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            The Menu
          </h2>
          <p
            className="text-black/50 text-sm max-w-md mx-auto mb-10"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            Every sandwich is built to order. Every ingredient is chosen with intent.
          </p>

          {/* Category Filter */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 text-xs tracking-[0.18em] uppercase font-bold rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-black text-white border-black"
                    : "bg-white text-black/50 border-black/20 hover:border-black hover:text-black"
                }`}
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item, idx) => (
            <MenuCard key={item.id} item={item} delay={idx * 70} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <a
            href="#contact"
            className="border border-black text-black text-xs tracking-[0.2em] uppercase font-bold px-8 py-4 hover:bg-black hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap rounded-sm inline-flex items-center gap-2"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            Order Inquiries
            <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function CategoryBadge({ category }: { category: string }) {
  if (category === "All Day") {
    return (
      <div
        className="flex items-center overflow-hidden rounded-sm text-[10px] tracking-[0.12em] uppercase font-bold"
        style={{ fontFamily: "'Barlow', sans-serif" }}
      >
        <span className="bg-amber-400 text-white px-2.5 py-1 flex items-center gap-1">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-sun-line" style={{ fontSize: "10px" }}></i>
          </span>
          AM
        </span>
        <span className="bg-neutral-800 text-white px-2.5 py-1 flex items-center gap-1">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-moon-line" style={{ fontSize: "10px" }}></i>
          </span>
          PM
        </span>
      </div>
    );
  }
  return (
    <span
      className="bg-black text-white text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 font-bold rounded-sm"
      style={{ fontFamily: "'Barlow', sans-serif" }}
    >
      {category}
    </span>
  );
}

function MenuCard({ item, delay }: { item: typeof menuItems[0]; delay: number }) {
  const [ref, visible] = useScrollAnimation();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className={`border border-black/10 rounded-sm overflow-hidden group hover:border-black transition-all duration-300 cursor-pointer flex flex-col ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms`, transitionDuration: "600ms" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="relative w-full h-56 overflow-hidden bg-[#f5f5f5]">
        <img
          src={item.image}
          alt={item.name}
          className={`w-full h-full object-cover ${item.imagePosition ?? "object-top"} group-hover:scale-105 transition-transform duration-500`}
        />
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <CategoryBadge category={item.category} />
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 bg-white flex-1 flex flex-col">
        <h3
          className="text-black font-bold text-lg leading-tight mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {item.name}
        </h3>
        <p
          className="text-black/55 text-xs leading-relaxed mb-4"
          style={{ fontFamily: "'Barlow', sans-serif" }}
        >
          {item.description}
        </p>

        {/* Ingredients */}
        <div className="mt-auto">
          <p
            className="text-black/30 text-[10px] tracking-[0.2em] uppercase mb-2 font-bold"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            Ingredients
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {item.ingredients.map((ing) => (
              <span
                key={ing}
                className="text-[10px] text-black/60 border border-black/15 px-2 py-0.5 rounded-full"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                {ing}
              </span>
            ))}
          </div>

          {/* Note */}
          {item.note && (
            <div
              className={`flex items-start gap-1.5 mt-3 pt-3 border-t border-black/8 transition-all duration-300 ${
                hovered ? "opacity-100" : "opacity-60"
              }`}
            >
              <i className="ri-information-line text-black/40 text-xs w-4 h-4 flex items-center justify-center mt-0.5"></i>
              <p
                className="text-black/40 text-[11px] leading-relaxed italic"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                {item.note}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
